import { useListDashboardsQuery } from "src/api/analytics-dashboard/dashboard";
import { useCaseAnalyticsAccess } from "src/cases/analytics/useCaseAnalyticsAccess";
import { Realm } from "src/clients/analytics-dashboard";
import { WorkspaceSimpleRoleType } from "src/clients/taktile-api";
import { useCapabilities } from "src/hooks/useCapabilities";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type CaseInsightsAccess = {
  canAccess: boolean;
  isLoading: boolean;
  invitedDashboardName: Nullable<string>;
};

export const useCaseInsightsAccess = (): CaseInsightsAccess => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const {
    caseAnalytics,
    workspaceRole,
    isLoading: isRoleLoading,
  } = useCapabilities();
  const { canAccess: isCaseAnalyticsEnabled } = useCaseAnalyticsAccess();

  const isAdmin = WorkspaceSimpleRoleType.ADMIN === workspaceRole;
  // Admins get every dashboard, editors get only the ones they are a member of,
  // so for an editor a non-empty list means they were invited.
  const needsInvite =
    isCaseAnalyticsEnabled && caseAnalytics.canAccess && !isAdmin;

  const { data: dashboards, isLoading: isDashboardsLoading } =
    useListDashboardsQuery(Realm.CASE_ANALYTICS, wsId, {
      enabled: needsInvite,
    });

  // Deny while loading, so the nav never shows an entry and then drops it.
  const isLoading = isRoleLoading || (needsInvite && isDashboardsLoading);

  return {
    canAccess:
      !isLoading &&
      isCaseAnalyticsEnabled &&
      caseAnalytics.canAccess &&
      (!needsInvite || (dashboards?.length ?? 0) > 0),
    isLoading,
    invitedDashboardName: dashboards?.[0]?.name ?? null,
  };
};
